import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  HOLDERS_STATES_VALUE_ENABLED,
  HOLDERS_STATES_VALUE_PENDING,
} from "@raiden/library/constants/holders";
import SubscriptionsBatchForm from "@raiden/library/containers/Subscriptions/Batch/Form";
import SubscriptionBatchHandler from "@raiden/library/containers/Subscriptions/Batch/Handler";
import { isCustomerFromGolbey } from "@raiden/library/helpers/customer/isCustomerFromGolbey";
import useAuthAs from "@raiden/library/hooks/auth/as";
import { useApi } from "@raiden/library/hooks/useApi";
import { usePreferredYear } from "@raiden/library/hooks/usePreferredYear";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  WIZARD_SUBSCRIPTIONS_LOCAL_STORAGE_KEY_INVOICE_ID,
  WIZARD_SUBSCRIPTIONS_SESSIONS_STORAGE_KEY_PRESTATION_ID,
} from "..";
import SWRHandler from "../../../components/SWR/SWRHandler";
import StepBottomBar from "../StepBottomBar";
import StepLayout from "../StepLayout";
import { getSubscriptionsCompatibleWithHolders } from "@raiden/library/helpers/subscriptions/getSubscriptionsCompatibleWithHolders";

/**
 * @param {object} params
 * @param {number} [params.initialPrestationId]
 * @param {import("@raiden/library/types/Subscription").Subscription[]} params.subscriptions
 * @param {import("@raiden/library/types/Holder").Holder[]} params.holders
 */
function getInitialSubscriptionsData({
  initialPrestationId,
  subscriptions,
  holders,
}) {
  /** @type {import("@raiden/library/containers/Subscriptions/Batch/Handler").SubscriptionsData} */
  const subscriptionsData = [];

  /** @type {import("@raiden/library/types/Prestation").PrestationHolderType[]} */
  let compatibleHolderTypes = [];

  if (initialPrestationId) {
    // find the subscription which has the initial prestation id
    const subscription = getSubscriptionsCompatibleWithHolders({
      subscriptions,
      holders,
    }).find((subscription) => {
      // find the prestation which has the initial prestation id
      const prestations = subscription.prestations ?? [];
      for (let i = 0; i < prestations.length; i++) {
        const prestation = prestations[i];
        if (prestation.id === initialPrestationId) {
          compatibleHolderTypes = prestation.holder_types;
          return true;
        }
      }
    });
    if (subscription) {
      const compatibleHolders = holders.filter((holder) =>
        compatibleHolderTypes.includes(holder.type),
      );
      if (compatibleHolderTypes.length > 0) {
        subscriptionsData.push({
          subscriptionId: String(subscription.id),
          holderIds: compatibleHolders.map((holder) => String(holder.id)),
        });
      }
    }
  }
  return subscriptionsData;
}

/**
 * @typedef {Object} Props
 * @property {() => void} nextStep
 *
 * @param {Props} props
 */
function StepSubscriptions({ nextStep }) {
  const intl = useIntl();

  const { user } = useAuthAs();

  const preferredYear = usePreferredYear();

  const [initialPrestationId] = useState(() => {
    if (typeof window !== "undefined") {
      const rawPrestationId = sessionStorage.getItem(
        WIZARD_SUBSCRIPTIONS_SESSIONS_STORAGE_KEY_PRESTATION_ID,
      );
      if (rawPrestationId) {
        const prestationId = parseInt(rawPrestationId);
        if (!isNaN(prestationId)) {
          return prestationId;
        }
      }
    }
  });

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Subscription").Subscription[]>} */
  const { swrResponse: swrResponseSubscriptions } = useApi(
    generateApiUri({
      id: "@subscriptions.viewAny",
      query: {
        subscribed_holders_year_id: preferredYear?.id,
        fields: [
          "subscription.prestations",
          "subscription.estimated_price",
          "subscription.prestations_holder_types",
          "subscription.subscribed_holders",
          "prestation.post",
          "post.current_revision",
          "post.revision.cover",
        ],
      },
    }),
  );

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Holder").Holder[]>} */
  const { swrResponse: swrResponseHolders } = useApi(
    user?.id
      ? generateApiUri({
          id: "@holders.viewAny",
          query: {
            customer_id: [user?.id],
            state: [HOLDERS_STATES_VALUE_ENABLED, HOLDERS_STATES_VALUE_PENDING],
          },
        })
      : null,
  );

  return (
    <StepLayout
      title={intl.formatMessage({
        defaultMessage: "Choix des adhésions annuelles",
      })}>
      <Stack spacing="1rem" px="1rem" mt="1rem">
        <Text colorScheme="gray">
          <FormattedMessage defaultMessage="Vous pouvez dès à présent choisir vos activités nécessitant une cotisation annuelle. Une fois votre compte activé, vous aurez également accès aux activités du planning (halte garderie, centre aéré, etc.)." />
        </Text>

        {isCustomerFromGolbey({ customer: user }) && (
          <Alert status="info">
            <AlertIcon />

            <AlertDescription>
              <FormattedMessage defaultMessage="Les habitants de Golbey peuvent bénéficier d'une aide de la Mairie sur les cotisations sous réserve de ne pas l'avoir perçue auprès d'un autre organisme. Se renseigner auprès du Secrétariat du Centre Social Louise Michel." />
            </AlertDescription>
          </Alert>
        )}
      </Stack>

      {user?.id && preferredYear && (
        <SWRHandler
          swrResponse={swrResponseSubscriptions}
          wrapper={(children) => <Box p="1rem">{children}</Box>}>
          {({ data: subscriptions }) => (
            <SWRHandler
              swrResponse={swrResponseHolders}
              shouldRenderWithEmptyData={true}
              wrapper={(children) => <Box p="1rem">{children}</Box>}>
              {({ data: holders }) => {
                return (
                  <SubscriptionBatchHandler
                    customerId={String(user.id)}
                    yearId={String(preferredYear.id)}
                    localStorageKey={
                      WIZARD_SUBSCRIPTIONS_LOCAL_STORAGE_KEY_INVOICE_ID
                    }
                    onSuccess={nextStep}
                    initialSubscriptionsData={getInitialSubscriptionsData({
                      subscriptions,
                      holders,
                      initialPrestationId,
                    })}
                    render={({
                      subscriptionsData,
                      upsertSubscriptionData,
                      deleteSubscriptionData,
                      submit,
                      isLoading,
                    }) => (
                      <>
                        <Stack spacing="1rem" p="1rem">
                          <SubscriptionsBatchForm
                            subscriptions={subscriptions}
                            holders={holders}
                            subscriptionsData={subscriptionsData}
                            upsertSubscriptionData={upsertSubscriptionData}
                            deleteSubscriptionData={deleteSubscriptionData}
                          />
                        </Stack>

                        <StepBottomBar>
                          <Button
                            onClick={submit}
                            isLoading={isLoading}
                            colorScheme="brandSecondary">
                            <FormattedMessage defaultMessage="Sauvegarder" />
                          </Button>
                        </StepBottomBar>
                      </>
                    )}
                  />
                );
              }}
            </SWRHandler>
          )}
        </SWRHandler>
      )}
    </StepLayout>
  );
}

export default StepSubscriptions;
